import { useI18n } from '@muse/i18n'

import { type AnalyticsPlacementTypes, sendEvent } from '@core/analytics'
import { useHydratedValue } from '@muse-web/router'
import type { TCollectionSimple } from '@core/models'
import { getAuthorPageUrl } from '@core/routes'
import { PlaybackButton } from '@ds/components'
import { setPlaybackContext } from '@features/audio-player'

import { CollectionTileCard } from '../components/collection-tile-card'
import { useCollectionPlayback } from '../hooks/useCollectionPlayback'
import { getCollectionPageUrl } from '../helpers/pageUrls'

export type TCollectionTileCardContainerProps = {
    collection: TCollectionSimple
    placement: AnalyticsPlacementTypes
}

export const CollectionTileCardContainer = ({
    collection,
    placement,
}: TCollectionTileCardContainerProps) => {
    const { t } = useI18n()
    const playback = useCollectionPlayback(collection)
    const isPlaying = useHydratedValue(playback.isPlaying, false)

    const handleCardClick = () => {
        sendEvent({ name: 'collection_click', payload: { collection: collection.id } })
    }

    const handlePlayClick = () => {
        sendEvent({
            name: 'play_button_click',
            payload: {
                collection: collection.id,
                params__key: ['player_placement'],
                params__str_value: [placement],
            },
        })
        setPlaybackContext('play', placement)
        playback.playPause()
    }

    return (
        <CollectionTileCard
            collectionId={collection.id}
            coverSrc={collection.imageUrl}
            coverAlt={collection.title}
            title={collection.title}
            titleHref={getCollectionPageUrl({
                authorUsername: collection.authorUsername,
                slug: collection.slug,
            })}
            authorLabel={collection.authorDisplayName}
            authorHref={getAuthorPageUrl({ authorUsername: collection.authorUsername })}
            onCardClick={handleCardClick}
            playSlot={
                collection.itemsCount > 0 ? (
                    <PlaybackButton
                        playing={isPlaying}
                        size="medium"
                        variant="colorful"
                        aria-label={isPlaying ? t('i18n.audioPause') : t('i18n.audioPlay')}
                        disabled={playback.isLoading}
                        onClick={handlePlayClick}
                    />
                ) : null
            }
        />
    )
}
